import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
  selectUserPreferences,
  updatePreference,
  saveUserPreferences,
  resetUserPreferences,
  clearError,
  selectPreferencesSaving,
  selectPreferencesError,
  selectPreferencesLastSynced,
  UserPreferences as UserPreferencesType
} from '../store/preferencesSlice';
import { useTranslation } from '../locales/useTranslation';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';
import {
  FiMonitor, FiBell, FiCheck, FiRotateCcw, FiX, FiSun, FiMoon,
} from 'react-icons/fi';

const UserPreferences: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const preferences = useSelector(selectUserPreferences);
  const { settings: systemSettings } = useSystemSettings();
  const newGuiRolledOut = systemSettings.NEW_GUI_ENABLED === 'true';
  const saving = useSelector(selectPreferencesSaving);
  const error = useSelector(selectPreferencesError);
  const lastSynced = useSelector(selectPreferencesLastSynced);
  const t = useTranslation();

  // Auto-save preferences immediately when changed
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Clear success message after timeout
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  // Handle preference changes - immediately dispatch to Redux for real-time UI updates
  const handlePreferenceChange = async (key: keyof UserPreferencesType, value: any) => {
    try {
      // Create updated preferences object
      const updatedPreferences = { ...preferences, [key]: value };

      // Immediately dispatch to Redux for real-time UI updates
      dispatch(updatePreference({ key, value }));

      // Auto-save to backend
      await dispatch(saveUserPreferences(updatedPreferences)).unwrap();
      setSaveSuccess(true);
    } catch (error) {
      console.error('Failed to save preference:', error);
      // Revert the change if save failed
      dispatch(updatePreference({ key, value: preferences[key] }));
    }
  };

  // Manual save for complex changes (if needed)
  const handleSave = async () => {
    try {
      await dispatch(saveUserPreferences(preferences)).unwrap();
      setSaveSuccess(true);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  // Reset to defaults
  const handleReset = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to reset all preferences to defaults? This action cannot be undone.'
    );

    if (confirmed) {
      try {
        await dispatch(resetUserPreferences()).unwrap();
        setSaveSuccess(true);
      } catch (error) {
        console.error('Failed to reset preferences:', error);
      }
    }
  };

  // Manual refresh (if needed)
  const handleRefresh = () => {
    dispatch(clearError());
  };

  // Format last synced time
  const formatLastSynced = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  if (useNew) {
    type Tone = 'accent' | 'success';
    const toneChip: Record<Tone, string> = { accent: 'bg-accent/15 text-accent', success: 'bg-success/15 text-success' };
    const toneBand: Record<Tone, string> = { accent: 'from-accent to-accent-hover', success: 'from-success to-success' };

    const ToggleRow: React.FC<{ tone: Tone; icon: React.ReactNode; title: string; desc: React.ReactNode; checked: boolean; onChange: (v: boolean) => void }> =
      ({ tone, icon, title, desc, checked, onChange }) => (
        <div className="flex items-center justify-between p-3 bg-surface-raised rounded border border-border-subtle">
          <div className="flex-1 pr-3">
            <div className="flex items-center mb-1">
              <div className={`p-1.5 rounded mr-2 ${toneChip[tone]}`}>{icon}</div>
              <h3 className="text-sm font-medium text-ink-primary">{title}</h3>
            </div>
            <p className="text-xs text-ink-secondary">{desc}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" className="sr-only peer" checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <div className="w-12 h-6 bg-border-strong rounded-full peer peer-checked:bg-success peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success/30 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full transition-colors" />
            <span className={`ml-2 text-xs font-medium ${checked ? 'text-success' : 'text-ink-secondary'}`}>{checked ? 'On' : 'Off'}</span>
          </label>
        </div>
      );

    const Section: React.FC<{ tone: Tone; icon: React.ReactNode; title: string; desc: string; children: React.ReactNode }> =
      ({ tone, icon, title, desc, children }) => (
        <div className="bg-surface border border-border-subtle rounded-lg overflow-hidden shadow-sm">
          <div className={`bg-gradient-to-r ${toneBand[tone]} px-4 py-3 flex items-center gap-2`}>
            {icon}
            <div>
              <h2 className="text-sm font-semibold text-white">{title}</h2>
              <p className="text-xs text-white/80">{desc}</p>
            </div>
          </div>
          <div className="p-4 space-y-3">{children}</div>
        </div>
      );

    return (
      <div className={`flex-1 p-6 font-plex transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <PageHeader title="Preferences" purpose="Customize your interface experience." />

        {error && (
          <div className="mb-4 p-4 bg-danger/10 border border-danger/30 rounded-lg flex items-center">
            <div className="text-danger font-medium flex-1">{error}</div>
            <button onClick={() => dispatch(clearError())} className="text-danger hover:opacity-75">
              <FiX />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Section tone="accent" icon={<FiMonitor className="text-white text-lg" />} title="Interface Settings" desc="Control appearance and navigation behavior">
            {/* Theme */}
            <div className="p-3 bg-surface-raised rounded border border-border-subtle">
              <h3 className="text-sm font-medium text-ink-primary mb-1">Theme</h3>
              <p className="text-xs text-ink-secondary mb-3">Choose your preferred color scheme</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'light', label: 'Light', icon: <FiSun />, desc: 'Clean & bright' },
                  { value: 'dark', label: 'Dark', icon: <FiMoon />, desc: 'Easy on eyes' },
                  { value: 'system', label: 'System', icon: <FiMonitor />, desc: 'Auto-switch' },
                ].map((theme) => (
                  <button
                    key={theme.value}
                    onClick={() => handlePreferenceChange('theme', theme.value)}
                    className={`relative p-2.5 rounded-lg border-2 transition-all text-center ${
                      preferences.theme === theme.value ? 'border-accent bg-accent/5' : 'border-border-subtle hover:border-border-strong'
                    }`}
                  >
                    <div className="flex justify-center text-lg text-ink-secondary mb-1">{theme.icon}</div>
                    <div className="text-xs font-medium text-ink-primary">{theme.label}</div>
                    <div className="text-[10px] text-ink-secondary">{theme.desc}</div>
                    {preferences.theme === theme.value && (
                      <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-accent rounded-full flex items-center justify-center">
                        <FiCheck className="text-white text-[10px]" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Font size */}
            <div className="p-3 bg-surface-raised rounded border border-border-subtle">
              <h3 className="text-sm font-medium text-ink-primary mb-1">Font Size</h3>
              <p className="text-xs text-ink-secondary mb-3">Adjust text size for better readability</p>
              <div className="flex gap-2">
                {['small', 'medium', 'large'].map((size) => (
                  <button
                    key={size}
                    onClick={() => handlePreferenceChange('fontSize', size)}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors capitalize ${
                      preferences.fontSize === size ? 'bg-accent/10 text-accent border-accent' : 'bg-surface text-ink-secondary border-border-subtle hover:border-border-strong'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <ToggleRow tone="accent" icon={<FiMonitor className="text-sm" />} title="Compact Mode" desc="Reduce spacing and padding for more content"
              checked={preferences.compactMode} onChange={(v) => handlePreferenceChange('compactMode', v)} />
            <ToggleRow tone="accent" icon={<FiMonitor className="text-sm" />} title="Auto-collapse Sidebar" desc="Automatically collapse sidebar when inactive"
              checked={preferences.sidebarAutoCollapse} onChange={(v) => handlePreferenceChange('sidebarAutoCollapse', v)} />
            <ToggleRow
              tone="accent" icon={<FiMonitor className="text-sm" />} title="New GUI Preview"
              desc={newGuiRolledOut ? 'Switch to the redesigned console.' : 'Not yet enabled org-wide — your choice will take effect once a superadmin turns it on.'}
              checked={!!preferences.useNewGui} onChange={(v) => handlePreferenceChange('useNewGui', v)}
            />
          </Section>

          <Section tone="success" icon={<FiBell className="text-white text-lg" />} title="Notification Settings" desc="Control how and when you receive notifications">
            <ToggleRow tone="success" icon={<FiBell className="text-sm" />} title="Show Notifications" desc="Display notifications bell in navigation bar"
              checked={preferences.showNotifications} onChange={(v) => handlePreferenceChange('showNotifications', v)} />
            <ToggleRow tone="success" icon={<FiBell className="text-sm" />} title="Notification Sounds" desc="Play sound effects for notifications"
              checked={preferences.notificationSounds} onChange={(v) => handlePreferenceChange('notificationSounds', v)} />
            <ToggleRow tone="success" icon={<FiMonitor className="text-sm" />} title="High Contrast" desc="Increase contrast for better visibility"
              checked={preferences.highContrast} onChange={(v) => handlePreferenceChange('highContrast', v)} />
            <ToggleRow tone="success" icon={<FiMonitor className="text-sm" />} title="Error Console" desc="Show error indicator in navigation bar"
              checked={preferences.showErrorConsole} onChange={(v) => handlePreferenceChange('showErrorConsole', v)} />
          </Section>
        </div>

        <div className="bg-surface border border-border-subtle rounded-lg p-4 flex justify-between items-center">
          <button
            onClick={handleReset}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-secondary bg-surface border border-border-strong rounded hover:bg-surface-raised disabled:opacity-50 transition-colors"
          >
            <FiRotateCcw /> Reset to Defaults
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-ink-secondary bg-surface border border-border-strong rounded hover:bg-surface-raised disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
            >
              <FiCheck /> {saving ? 'Applying…' : 'Apply'}
            </button>
            <button
              onClick={async () => { await handleSave(); navigate(-1); }}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-success rounded hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              <FiCheck /> {saving ? 'Applying…' : 'Apply & Close'}
            </button>
          </div>
        </div>

        <p className="text-center mt-6 text-ink-secondary text-xs">
          Preferences are automatically synced and backed up to your account. Changes take effect immediately across all your devices.
        </p>
      </div>
    );
  }

  // Modal Structure
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">User Preferences</h1>
                <p className="text-blue-100 text-sm">Customize your interface experience</p>
              </div>
            </div>

            {/* Close Button - Clearly Visible */}
            <button
              onClick={() => window.history.back()}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg p-2 transition-all duration-200 border-2 border-gray-400 hover:border-gray-500 shadow-sm"
              title="Close Preferences"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Add spacing after header */}
        <div className="h-2"></div>

        {/* Toast Location Indicator */}
        <div className="flex justify-end px-6 mb-2">
          <div className="flex items-center text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Toast notifications appear here →
          </div>
        </div>



        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <div className="text-red-500 mr-3">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="text-red-800 font-medium">{error}</div>
              <button
                onClick={() => dispatch(clearError())}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Preferences Sections - Wide side-by-side layout */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-4 overflow-y-auto min-h-0">

          {/* Interface Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Interface Settings
              </h2>
              <p className="text-blue-100 text-xs mt-0.5">
                Control appearance and navigation behavior
              </p>
            </div>

            <div className="p-4 space-y-4">

              {/* Theme Selection */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex items-center mb-3">
                  <div className="bg-purple-100 dark:bg-purple-900 p-1.5 rounded mr-2">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Theme</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-xs mb-4">
                  Choose your preferred color scheme
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {
                      value: 'light',
                      label: 'Light',
                      icon: '☀️',
                      preview: 'bg-white border-gray-300 text-gray-900',
                      desc: 'Clean & bright'
                    },
                    {
                      value: 'dark',
                      label: 'Dark',
                      icon: '🌙',
                      preview: 'bg-gray-900 border-gray-700 text-gray-100',
                      desc: 'Easy on eyes'
                    },
                    {
                      value: 'system',
                      label: 'System',
                      icon: '💻',
                      preview: 'bg-gradient-to-r from-white to-gray-100 border-gray-300 text-gray-900',
                      desc: 'Auto-switch'
                    }
                  ].map((theme) => (
                    <button
                      key={theme.value}
                      onClick={() => handlePreferenceChange('theme', theme.value)}
                      className={`relative p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                        preferences.theme === theme.value
                          ? 'border-blue-500 shadow-lg ring-2 ring-blue-200'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      {/* Theme Preview */}
                      <div className={`w-full h-8 rounded mb-2 ${theme.preview} border flex items-center justify-center text-xs font-medium`}>
                        <span className="text-lg">{theme.icon}</span>
                      </div>

                      {/* Theme Info */}
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{theme.label}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{theme.desc}</p>
                      </div>

                      {/* Selected Indicator */}
                      {preferences.theme === theme.value && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size Selection */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex items-center mb-2">
                  <div className="bg-green-100 dark:bg-green-900 p-1.5 rounded mr-2">
                    <svg className="w-4 h-4 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                  </div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Font Size</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
                  Adjust text size for better readability
                </p>
                <div className="flex gap-2">
                  {[
                    { value: 'small', label: 'Small', sample: 'Aa' },
                    { value: 'medium', label: 'Medium', sample: 'Aa' },
                    { value: 'large', label: 'Large', sample: 'Aa' }
                  ].map((size) => (
                    <button
                      key={size.value}
                      onClick={() => handlePreferenceChange('fontSize', size.value)}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        preferences.fontSize === size.value
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-2 border-blue-300 dark:border-blue-600'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span className={`mr-2 ${
                        size.value === 'small' ? 'text-sm' :
                        size.value === 'large' ? 'text-lg' : 'text-base'
                      }`}>{size.sample}</span>
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Compact Mode */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex-1 pr-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-orange-100 dark:bg-orange-900 p-1.5 rounded mr-2">
                      <svg className="w-4 h-4 text-orange-600 dark:text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Compact Mode</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Reduce spacing and padding for more content
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {/* Enhanced Slide Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.compactMode}
                      onChange={(e) => handlePreferenceChange('compactMode', e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-green-500 peer-checked:border-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full after:border-gray-300 peer-checked:after:border-white border-2 border-gray-400"></div>
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      preferences.compactMode
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {preferences.compactMode ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Sidebar Auto-collapse */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex-1 pr-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-indigo-100 dark:bg-indigo-900 p-1.5 rounded mr-2">
                      <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Auto-collapse Sidebar</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Automatically collapse sidebar when inactive (Demo: Click to test)
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {/* Enhanced Slide Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.sidebarAutoCollapse}
                      onChange={(e) => handlePreferenceChange('sidebarAutoCollapse', e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-green-500 peer-checked:border-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full after:border-gray-300 peer-checked:after:border-white border-2 border-gray-400"></div>
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      preferences.sidebarAutoCollapse
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {preferences.sidebarAutoCollapse ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>

              {/* New GUI Preview */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <div className="flex items-center mb-1">
                      <div className="bg-blue-100 dark:bg-blue-900 p-1.5 rounded mr-2">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">New GUI Preview</h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-xs">
                      {newGuiRolledOut
                        ? 'Switch to the redesigned console.'
                        : 'Not yet enabled org-wide — your choice will take effect once a superadmin turns it on.'}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={!!preferences.useNewGui}
                        onChange={(e) => handlePreferenceChange('useNewGui', e.target.checked)}
                      />
                      <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-green-500 peer-checked:border-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full after:border-gray-300 peer-checked:after:border-white border-2 border-gray-400"></div>
                      <span className={`ml-3 text-sm font-medium transition-colors ${
                        preferences.useNewGui
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {preferences.useNewGui ? 'New' : 'Classic'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Notification Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-3">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.868 12.683A17.925 17.925 0 0112 21c7.962 0 12-1.21 12-2.683m-12 2.683a17.925 17.925 0 01-7.132-8.317M12 21c4.411 0 8-4.03 8-9s-3.589-9-8-9-8 4.03-8 9a9.06 9.06 0 001.832 5.683L4 21l4.868-8.317z" />
                </svg>
                Notification Settings
              </h2>
              <p className="text-green-100 text-xs mt-0.5">
                Control how and when you receive notifications
              </p>
            </div>

            <div className="p-4 space-y-4">

              {/* Show Notifications Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex-1 pr-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-blue-100 dark:bg-blue-900 p-1.5 rounded mr-2">
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.868 12.683A17.925 17.925 0 0112 21c7.962 0 12-1.21 12-2.683m-12 2.683a17.925 17.925 0 01-7.132-8.317M12 21c4.411 0 8-4.03 8-9s-3.589-9-8-9-8 4.03-8 9a9.06 9.06 0 001.832 5.683L4 21l4.868-8.317z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Show Notifications</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Display notifications bell in navigation bar
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {/* Enhanced Slide Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.showNotifications}
                      onChange={(e) => handlePreferenceChange('showNotifications', e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-green-500 peer-checked:border-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full after:border-gray-300 peer-checked:after:border-white border-2 border-gray-400"></div>
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      preferences.showNotifications
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {preferences.showNotifications ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Notification Sounds */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex-1 pr-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-yellow-100 dark:bg-yellow-900 p-1.5 rounded mr-2">
                      <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Notification Sounds</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Play sound effects for notifications
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {/* Enhanced Slide Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.notificationSounds}
                      onChange={(e) => handlePreferenceChange('notificationSounds', e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 border-2 border-gray-400 peer-checked:border-green-500"></div>
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      preferences.notificationSounds
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {preferences.notificationSounds ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>

              {/* High Contrast Mode */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex-1 pr-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-red-100 dark:bg-red-900 p-1.5 rounded mr-2">
                      <svg className="w-4 h-4 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">High Contrast</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Increase contrast for better visibility
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {/* Enhanced Slide Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.highContrast}
                      onChange={(e) => handlePreferenceChange('highContrast', e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 border-2 border-gray-400 peer-checked:border-green-500"></div>
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      preferences.highContrast
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {preferences.highContrast ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Error Console Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <div className="flex-1 pr-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-orange-100 dark:bg-orange-900 p-1.5 rounded mr-2">
                      <svg className="w-4 h-4 text-orange-600 dark:text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Error Console</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Show error indicator in navigation bar
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {/* Enhanced Slide Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.showErrorConsole}
                      onChange={(e) => handlePreferenceChange('showErrorConsole', e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 border-2 border-gray-400 peer-checked:border-green-500"></div>
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      preferences.showErrorConsole
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {preferences.showErrorConsole ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* Action Buttons */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex justify-between items-center">

            {/* Left side - Reset button */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleReset}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset to Defaults
              </button>
            </div>

            {/* Right side - Action buttons */}
            <div className="flex space-x-3">
              {/* Cancel Button */}
              <button
                onClick={() => window.history.back()}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>

              {/* Apply Button */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 border border-transparent rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Apply
                  </>
                )}
              </button>

              {/* Apply & Close Button */}
              <button
                onClick={async () => {
                  await handleSave();
                  setTimeout(() => window.history.back(), 500);
                }}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 border border-transparent rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7M6 6l12 12" />
                    </svg>
                    Apply & Close
                  </>
                )}
              </button>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Preferences are automatically synced and backed up to your account.</p>
          <p className="mt-1">Changes take effect immediately across all your devices.</p>
        </div>

      </div>
    </div>
  );
};

export default UserPreferences;
